import {useState, useEffect, useCallback, useMemo} from "react";

const useUrlHash = () => {
    const getSegments = useCallback(() =>
            window.location.hash.replace(/^#\/?/, '').split('/').filter(Boolean),
        []);

    const [segments, setSegments] = useState(getSegments());

    useEffect(() => {
        const handleHashChange = () => {
            const nextSegments = getSegments();
            setSegments(prev => {
                const isSame = JSON.stringify(prev) === JSON.stringify(nextSegments);
                return isSame ? prev : nextSegments;
            });
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [getSegments]);

    const updateHash = useCallback((...args) => {
        const current = getSegments();
        const incoming = args.flat();
        let newSegments = [];

        for (let i = 0; i < incoming.length; i++) {
            const incomingVal = incoming[i];

            if (incomingVal === null) break;

            const segment = (incomingVal !== undefined && incomingVal !== '')
                ? incomingVal.toString().replace('#', '')
                : current[i];

            if (segment !== undefined && segment !== null) {
                newSegments.push(segment);
            }
        }

        const newHashString = '/' + newSegments.join('/');

        const applyUpdate = () => {
            window.location.hash = newHashString;
        };

        if (typeof window.questionClusters?.warnUnsubmittedWorking === 'function') {
            window.questionClusters.warnUnsubmittedWorking(applyUpdate);
        } else {
            applyUpdate();
        }
    }, [getSegments]);

    const findTabByHash = useCallback((tabs, hash) => {
        let matchedTab = null;
        let bestMatch = -1;

        tabs.forEach(tab => {
            if (!tab.hash) return;

            const isMatch = tab.hash.every((part, i) => part === hash[i]);

            if (isMatch && tab.hash.length > bestMatch) {
                matchedTab = tab;
                bestMatch = tab.hash.length;
            }
        });
        return matchedTab;
    }, []);

    return useMemo(() => ({
        hashSegments: segments,
        hashString: segments.join('/'),
        primarySegment: segments[0] || null,
        findTabByHash,
        updateHash,
    }), [segments, findTabByHash, updateHash]);
};

export default useUrlHash;